   import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type LayerConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { useAuthStore } from '../../../stores';
import { PointLayerItem } from './PointLayerItem';
import { PolygonLayerItem } from './PolygonLayerItem';
import { DropdownMenu, type DropdownMenuItem } from './DropdownMenu';
import { LayerLoaderModal } from './LayerLoaderModal';
import { DataTableModal } from './DataTableModal';
import { loadLayerData } from '../services';
import './LayerController.css';

/**
 * Returns a human-readable relative time string from a timestamp.
 */
function formatRelativeTime(timestamp: number | null): string {
  if (!timestamp) return 'No changes yet';

  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'Last edit was < 1m ago';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Last edit was ${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Last edit was ${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `Last edit was ${days}d ago`;
}

/**
 * Sortable layer item wrapper component
 */
interface SortableLayerItemProps {
  layer: LayerConfig;
  onRemove: (layerId: string) => void;
  children: (settingsOpen: boolean, onSettingsClosed: () => void) => React.ReactNode;
}

const SortableLayerItem: React.FC<SortableLayerItemProps> = ({ layer, onRemove, children }) => {
  const updateLayerConfig = useMapStore((state) => state.updateLayerConfig);
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDataTableOpen, setIsDataTableOpen] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleToggleVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateLayerConfig(layer.id, { visible: e.target.checked });
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLayerMenuOpen(!isLayerMenuOpen);
  };

  const layerMenuItems: DropdownMenuItem[] = [
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ),
      label: 'Layer Settings',
      onClick: () => setIsSettingsOpen(true),
    },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="3" y1="9" x2="21" y2="9"></line>
          <line x1="3" y1="15" x2="21" y2="15"></line>
          <line x1="9" y1="3" x2="9" y2="21"></line>
          <line x1="15" y1="3" x2="15" y2="21"></line>
        </svg>
      ),
      label: 'View Data',
      onClick: () => setIsDataTableOpen(true),
    },
    { type: 'divider' },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      ),
      label: 'Remove Layer',
      onClick: () => onRemove(layer.id),
    },
  ];

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="layer-item"
    >
      <div className="layer-header">
        <input 
          type="checkbox" 
          id={layer.id} 
          name={layer.id} 
          checked={layer.visible} 
          onChange={handleToggleVisibility}
        />
        <span 
          {...attributes}
          {...listeners}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', flex: 1 }}
        >
          {layer.name}
        </span>
        <div style={{ position: 'relative' }}>
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="layer-menu"
            onClick={handleMenuClick}
          >
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>
          <DropdownMenu
            isOpen={isLayerMenuOpen}
            onClose={() => setIsLayerMenuOpen(false)}
            items={layerMenuItems}
          />
        </div>
      </div>
      <div 
        className="layer-details"
        {...attributes}
        {...listeners}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {children(isSettingsOpen, () => setIsSettingsOpen(false))}
      </div>

      {isDataTableOpen && (
        <DataTableModal
          isOpen={isDataTableOpen}
          onClose={() => setIsDataTableOpen(false)}
          layer={layer}
        />
      )}
    </div>
  );
};

/**
 * Component for managing map layers dynamically
 * Allows adding and removing layers with controls
 */
export const LayerController: React.FC = () => {
  const { addLayer, removeLayer, reorderLayers, layerIds, layersConfig, lastSettingChange } = useMapStore();
  const logout = useAuthStore((state) => state.logout);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLayerLoaderOpen, setIsLayerLoaderOpen] = useState(false);
  const [, setTick] = useState(0);

  // Re-render periodically to keep the relative time display up to date
  useEffect(() => {
    if (!lastSettingChange) return;
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, [lastSettingChange]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleAddLayer = () => {
    setIsLayerLoaderOpen(true);
  };

  const handleLoadLayers = async (configs: LayerConfig[]) => {
    console.log('[LayerController] handleLoadLayers called with configs:', configs);
    for (const config of configs) {
      try {
        // Get current viewport from store
        const currentViewport = useMapStore.getState().viewport;
        console.log('[LayerController] Current viewport:', currentViewport);
        
        // Calculate viewport bounds
        const worldSize = 256 * Math.pow(2, currentViewport.zoom);
        const lonDegreesPerPixel = 360 / worldSize;
        const latRadians = currentViewport.latitude * Math.PI / 180;
        const latDegreesPerPixel = (360 / worldSize) / Math.cos(latRadians);
        
        const lonOffset = (window.innerWidth / 2) * lonDegreesPerPixel * 1.1;
        const latOffset = (window.innerHeight / 2) * latDegreesPerPixel * 1.1;
        
        const bounds = {
          north: Math.min(currentViewport.latitude + latOffset, 85),
          south: Math.max(currentViewport.latitude - latOffset, -85),
          east: currentViewport.longitude + lonOffset,
          west: currentViewport.longitude - lonOffset,
        };
        
        const { data } = await loadLayerData(config, {
          ...currentViewport,
          bounds,
        });
        addLayer(config, data);
      } catch (error) {
        console.error('Failed to load layer:', config.name, error);
      }
    }
  };

  const handleRemoveLayer = (layerId: string) => {
    removeLayer(layerId);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = layerIds.indexOf(active.id as string);
      const newIndex = layerIds.indexOf(over.id as string);
      const newOrder = arrayMove(layerIds, oldIndex, newIndex);
      reorderLayers(newOrder);
    }
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const menuItems: DropdownMenuItem[] = [
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ),
      label: 'Global Config',
      onClick: () => console.log('Global Config clicked'),
    },
    { type: 'divider' },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
          <polyline points="17 21 17 13 7 13 7 21"></polyline>
          <polyline points="7 3 7 8 15 8"></polyline>
        </svg>
      ),
      label: 'Save',
      onClick: () => console.log('Save clicked'),
      disabled: true,
    },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 4 1 10 7 10"></polyline>
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
        </svg>
      ),
      label: 'Change Version',
      onClick: () => console.log('Change Version clicked'),
      disabled: true,
    },
    { type: 'divider' },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="12" y1="18" x2="12" y2="12"></line>
          <line x1="9" y1="15" x2="15" y2="15"></line>
        </svg>
      ),
      label: 'Load Map',
      onClick: () => console.log('Load Map clicked'),
      disabled: true,
    },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      ),
      label: 'Delete Map',
      onClick: () => console.log('Delete Map clicked'),
      disabled: true,
    },
    { type: 'divider' },
    {
      type: 'item',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          <polyline points="16 17 21 12 16 7"></polyline>
          <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>
      ),
      label: 'Logout',
      onClick: () => logout(),
    },
  ];

  return (
    <div id="layers">
      <div className="title-container">
        <div className="title-text">
          <h3>MaxHealth Market Map</h3>
          <p>{formatRelativeTime(lastSettingChange)}</p>
        </div>
        <div className="title-menu">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            onClick={toggleMenu}
          >
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>

          <DropdownMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            items={menuItems}
          />
        </div>
      </div>

      {!isCollapsed && (
        <div className="collapsible-content" id="collapsible-content">
          <div className="actions-bar">
            <button className="action-btn" onClick={handleAddLayer}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14"></path>
              </svg>
              Add layer
            </button>
            <button
              className="action-btn action-btn--disabled"
              disabled
              title="Coming soon"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              Share
            </button>
            <button
              className="action-btn action-btn--disabled"
              disabled
              title="Coming soon"
            >
              <span style={{ fontSize: "16px" }}>📄</span>
              PDF
            </button>
          </div>

          <hr className="layer-separator" />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragEnd}
          >
            <SortableContext
              items={layerIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="layer-list">
                {layerIds.map((id: string) => {
                  const layer = layersConfig[id];

                  // Detect layer type and render corresponding component
                  // point and heatmap both use PointLayerItem
                  if (layer.type === "points") {
                    return (
                      <SortableLayerItem
                        key={layer.id}
                        layer={layer}
                        onRemove={handleRemoveLayer}
                      >
                        {(settingsOpen, onSettingsClosed) => (
                          <PointLayerItem
                            layer={layer}
                            settingsOpen={settingsOpen}
                            onSettingsClosed={onSettingsClosed}
                          />
                        )}
                      </SortableLayerItem>
                    );
                  } else if (layer.type === "polygons") {
                    return (
                      <SortableLayerItem
                        key={layer.id}
                        layer={layer}
                        onRemove={handleRemoveLayer}
                      >
                        {() => <PolygonLayerItem layer={layer} />}
                      </SortableLayerItem>
                    );
                  }

                  // Fallback for unknown types
                  return null;
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div
        className={`collapse-bar ${isCollapsed ? "collapsed" : ""}`}
        id="collapse-bar"
        onClick={toggleCollapse}
      >
        <span>{isCollapsed ? "Expand" : "Collapse"}</span>
        <svg
          className="collapse-icon"
          id="collapse-icon"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      <LayerLoaderModal
        isOpen={isLayerLoaderOpen}
        onClose={() => setIsLayerLoaderOpen(false)}
        onLoadLayers={handleLoadLayers}
        loadedLayerIds={layerIds}
      />
    </div>
  );
};
